import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Modal } from 'react-bootstrap'
import { Prompt, useHistory, useLocation } from 'react-router-dom'

import { groupBy, sortBy } from 'lodash'
import get from 'lodash/fp/get'
import Moment from 'moment'
import { set } from 'object-path-immutable'

import AdjustersService from '../../../services/AdjustersService'
import AppointmentsService from '../../../services/AppointmentsService'
import CasesService from '../../../services/CasesService'
import ReferralSourcesService from '../../../services/ReferralSourcesService'
import TasksService from '../../../services/TasksService'
import { cleanAndNormalizeCaseForSave } from '../../../utils/CaseUtils'
import { cleanPhone } from '../../../utils/PhoneUtils'
import validations from '../../../validation'
import LoadingSpinner from '../../LoadingSpinner'
import NewAdjusterModal from '../../modals/NewAdjusterModal'
import NewCompanyModal from '../../modals/NewCompanyModal'

import Sections from './Sections'

import './DetailsPage.scss'

const isObjectEmpty = obj => {
  if (!obj) return true
  return Object.entries(obj).every(([key, val]) => {
    if (key === 'isActual') return true
    if (key === 'lightDuty' && (val === false || val === null || val === undefined)) return true
    if (val === null || val === undefined) return true
    if (typeof val === 'string' && val.trim() === '') return true
    if (Array.isArray(val) && val.length === 0) return true
    return false
  })
}

const cleanModelForComparison = model => {
  if (!model) return model
  const cleaned = { ...model }

  const listFields = [
    'diagnosis',
    'surgeriesAndHospitalizations',
    'medications',
    'tests',
    'allergies',
    'providers',
    'intakeActivities',
    'appointments',
    'equipment',
    'transportation',
    'translationServices',
    'homeSetup',
  ]

  listFields.forEach(field => {
    if (Array.isArray(cleaned[field])) {
      cleaned[field] = cleaned[field].filter(x => !isObjectEmpty(x))
    }
  })

  // Linked providers are saved directly to the database via ProviderService (link/unlink)
  // Exclude them from Case Details form dirty checking so linking/unlinking does not mark form dirty
  delete cleaned.treatmentProviders
  delete cleaned.equipmentProviders
  delete cleaned.pharmacies

  if (cleaned.mmi && Array.isArray(cleaned.mmi.history)) {
    cleaned.mmi = {
      ...cleaned.mmi,
      history: cleaned.mmi.history.filter(x => !isObjectEmpty(x)),
    }
  }

  if (cleaned.status && Array.isArray(cleaned.status.history)) {
    cleaned.status = {
      ...cleaned.status,
      history: cleaned.status.history.filter(x => !isObjectEmpty(x)),
    }
  }

  return cleaned
}

const isModelActuallyDirty = (current, original) => {
  if (!current || !original) return false
  const cleanCurrent = cleanModelForComparison(current)
  const cleanOriginal = cleanModelForComparison(original)
  return JSON.stringify(cleanCurrent) !== JSON.stringify(cleanOriginal)
}

function validateModel(caseModel) {
  const result = {
    success: true,
  }

  const modelToValidate = cleanAndNormalizeCaseForSave(caseModel)

  Sections.forEach((section, index) => {
    const schema = section.validationSchema || validations.object({})
    const isValid = validations.validate(modelToValidate, schema)
    result[index] = isValid
    result.success = result.success && isValid.success
  })

  return result
}

function sortByName(array) {
  return sortBy(array, ['name'])
}

const STATE_MAP = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
  'american samoa': 'AS',
  guam: 'GU',
  'northern mariana islands': 'MP',
  'puerto rico': 'PR',
  'virgin islands': 'VI',
  'federated states of micronesia': 'FM',
  'marshall islands': 'MH',
  palau: 'PW',
  'armed forces americas': 'AA',
  'armed forces europe': 'AE',
  'armed forces pacific': 'AP',
}

function normalizeCaseStates(caseModel) {
  if (!caseModel) return caseModel
  let model = { ...caseModel }

  const normalize = state => {
    if (!state) return state
    const cleaned = state.trim().toLowerCase()
    if (cleaned.length === 2) return state.toUpperCase()
    return STATE_MAP[cleaned] || state
  }

  if (model.client?.address?.state) {
    model = set(model, 'client.address.state', normalize(model.client.address.state))
  }
  if (model.employer?.address?.state) {
    model = set(model, 'employer.address.state', normalize(model.employer.address.state))
  }
  if (model.plaintiff?.address?.state) {
    model = set(model, 'plaintiff.address.state', normalize(model.plaintiff.address.state))
  }
  if (model.defendant?.address?.state) {
    model = set(model, 'defendant.address.state', normalize(model.defendant.address.state))
  }

  return model
}

Sections.forEach((section, i) => {
  section.originalIndex = i
})

const DetailsPage = ({ caseNumber, showError, formRef, user, onDirtyStateChange }) => {
  const history = useHistory()
  const location = useLocation()

  const [isLoading, setIsLoading] = useState(true)
  const [caseModel, setCaseModel] = useState()
  const [referralSources, setReferralSources] = useState([])
  const [adjusters, setAdjusters] = useState([])
  const [files, setFiles] = useState([])
  const [appointments, setAppointments] = useState([])
  const [tasks, setTasks] = useState([])
  const [namedState, setNamedState] = useState({})
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const [selectedSection, setSelectedSection] = useState(() => {
    const param = new URLSearchParams(location.search).get('section')
    const index = param !== null ? parseInt(param, 10) : 0
    // Clamp to valid bounds — an out-of-range or non-numeric ?section=N param
    // would make Sections[index] undefined and trigger a TypeError→render-loop→OOM.
    return Number.isFinite(index) && index >= 0 && index < Sections.length ? index : 0
  })
  const [selectedAdjuster, setSelectedAdjuster] = useState({})
  const [selectedReferralSource, setSelectedReferralSource] = useState({})
  const [touched, setTouched] = useState({})
  const [modelIsDirty, setModelIsDirty] = useState(false)
  const [originalCaseModel, setOriginalCaseModel] = useState()
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false)
  const [pendingLocation, setPendingLocation] = useState(null)
  const isLeavingRef = React.useRef(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [validStateBySection, setValidStateBySection] = useState()
  const [isSidebarToggled, setIsSidebarToggled] = useState(true)

  const [showNewReferralSourceModal, setShowNewReferralSourceModal] = useState(false)
  const [showNewAdjusterModal, setShowNewAdjusterModal] = useState(false)

  useEffect(() => {
    const casePromise = CasesService.get(caseNumber).then(caseModel => {
      // Migrate legacy data: status was formerly stored as a bare array of history entries
      // (e.g. status: [{workStatus: '...', dateLastWorked: '...'}]).
      // WorkStatusSection now expects status: { history: [...] }.
      // Without this, the yup schema fails causing the sidebar to show Work Status as red,
      // validateModel blocks saves from any section, and saving produces
      // NoAdditionalPropertiesAllowed because status[0], status[1], etc. remain in the payload.
      let migratedModel = Array.isArray(caseModel.status)
        ? { ...caseModel, status: { history: caseModel.status } }
        : typeof caseModel.status === 'string'
        ? { ...caseModel, status: null } // lifecycle string was previously injected here; null = no work status data
        : caseModel

      // Ensure status.history is populated if scalar work status fields exist but history is empty or absent
      if (migratedModel.status && typeof migratedModel.status === 'object') {
        const hasHistoryArray = Array.isArray(migratedModel.status.history)
        const historyLen = hasHistoryArray ? migratedModel.status.history.length : 0

        if (!hasHistoryArray || historyLen === 0) {
          const wsObj =
            migratedModel.workStatus && typeof migratedModel.workStatus === 'object' ? migratedModel.workStatus : {}
          const cap = migratedModel.status.workStatus || wsObj.workCapacity || wsObj.status || null
          const retStatus = migratedModel.status.returnStatus || wsObj.restrictions || null
          const effDate = migratedModel.status.date || wsObj.effectiveDate || null
          const provider = migratedModel.status.treatmentProvider || null
          const treatment = migratedModel.status.currentTreatment || null
          const dateLastWorked = migratedModel.status.dateLastWorked || null
          const lightDuty = Boolean(
            migratedModel.status.lightDuty ||
              (retStatus && /light|desk|modified/i.test(retStatus)) ||
              (cap && /light|desk|modified/i.test(cap)),
          )

          if (cap || retStatus || effDate || provider || treatment || dateLastWorked) {
            migratedModel = {
              ...migratedModel,
              status: {
                ...migratedModel.status,
                history: [
                  {
                    dateLastWorked,
                    date: effDate,
                    treatmentProvider: provider,
                    currentTreatment: treatment,
                    workStatus: cap,
                    returnStatus: retStatus,
                    lightDuty,
                  },
                ],
              },
            }
          } else if (!hasHistoryArray) {
            migratedModel = {
              ...migratedModel,
              status: {
                ...migratedModel.status,
                history: [],
              },
            }
          }
        }
      } else if (!migratedModel.status && migratedModel.workStatus && typeof migratedModel.workStatus === 'object') {
        const wsObj = migratedModel.workStatus
        const cap = wsObj.workCapacity || wsObj.status || null
        const retStatus = wsObj.restrictions || null
        const effDate = wsObj.effectiveDate || null
        if (cap || retStatus || effDate) {
          const isLightDuty = Boolean(
            (retStatus && /light|desk|modified/i.test(retStatus)) || (cap && /light|desk|modified/i.test(cap)),
          )
          migratedModel = {
            ...migratedModel,
            status: {
              workStatus: cap,
              returnStatus: retStatus,
              date: effDate,
              lightDuty: isLightDuty,
              history: [
                {
                  dateLastWorked: null,
                  date: effDate,
                  treatmentProvider: null,
                  currentTreatment: null,
                  workStatus: cap,
                  returnStatus: retStatus,
                  lightDuty: isLightDuty,
                },
              ],
            },
          }
        }
      }

      // Ensure mmi.history is populated if scalar MMI fields exist but history is empty or absent
      if (migratedModel.mmi && typeof migratedModel.mmi === 'object') {
        const hasMmiHistoryArray = Array.isArray(migratedModel.mmi.history)
        const mmiHistoryLen = hasMmiHistoryArray ? migratedModel.mmi.history.length : 0

        if (!hasMmiHistoryArray || mmiHistoryLen === 0) {
          const mmiDate =
            migratedModel.mmi.date || migratedModel.mmi.mmiDate || migratedModel.functional?.estimatedDateOfMMI || null
          const rawReached =
            typeof migratedModel.mmi.reached === 'boolean'
              ? migratedModel.mmi.reached
              : typeof migratedModel.mmi.isMmiReached === 'boolean'
              ? migratedModel.mmi.isMmiReached
              : null
          const rating = migratedModel.mmi.impairmentRating
          let narrative =
            typeof migratedModel.functional?.mmiNarrative === 'string' && migratedModel.functional.mmiNarrative.trim()
              ? migratedModel.functional.mmiNarrative.trim()
              : null
          if (!narrative && (mmiDate || rawReached !== null)) {
            const parts = []
            if (rawReached !== null) {
              parts.push(`MMI: ${rawReached ? 'Reached' : 'Not Reached'}`)
            } else {
              parts.push('MMI Evaluation')
            }
            if (mmiDate) {
              parts.push(`on ${mmiDate}`)
            }
            if (rating) {
              parts.push(`• Impairment: ${rating}%`)
            }
            narrative = parts.join(' ')
          }

          if (mmiDate || rawReached !== null || narrative) {
            migratedModel = {
              ...migratedModel,
              mmi: {
                ...migratedModel.mmi,
                history: [
                  {
                    date: mmiDate,
                    narrative,
                    isActual: rawReached === true,
                  },
                ],
              },
            }
          } else if (!hasMmiHistoryArray) {
            migratedModel = {
              ...migratedModel,
              mmi: {
                ...migratedModel.mmi,
                history: [],
              },
            }
          }
        }
      } else if (!migratedModel.mmi && migratedModel.functional?.estimatedDateOfMMI) {
        const estDate = migratedModel.functional.estimatedDateOfMMI
        const narrative = migratedModel.functional?.mmiNarrative || `MMI Evaluation on ${estDate}`
        migratedModel = {
          ...migratedModel,
          mmi: {
            date: estDate,
            reached: false,
            isMmiReached: false,
            history: [
              {
                date: estDate,
                narrative,
                isActual: false,
              },
            ],
          },
        }
      }

      // Normalize legacy full-length state names to 2-character abbreviations
      // to prevent max(2) validation errors on address states.
      migratedModel = normalizeCaseStates(migratedModel)

      // Coerce list fields to arrays to satisfy JSON schema validation
      const listFields = [
        'surgeriesAndHospitalizations',
        'tests',
        'medications',
        'allergies',
        'diagnosis',
        'providers',
        'intakeActivities',
        'appointments',
        'treatmentProviders',
        'equipmentProviders',
        'pharmacies',
        'equipment',
        'transportation',
        'translationServices',
        'homeSetup',
      ]
      listFields.forEach(field => {
        if (!Array.isArray(migratedModel[field])) {
          migratedModel[field] = []
        }
      })

      if (migratedModel.status && !Array.isArray(migratedModel.status.history)) {
        migratedModel.status.history = []
      }

      if (migratedModel.mmi && !Array.isArray(migratedModel.mmi.history)) {
        migratedModel.mmi.history = []
      }

      if (Array.isArray(migratedModel.diagnosis)) {
        migratedModel.diagnosis.forEach(diag => {
          delete diag.category
          delete diag.moi
          delete diag.noi
        })
      }

      if (Array.isArray(migratedModel.surgeriesAndHospitalizations)) {
        migratedModel.surgeriesAndHospitalizations.forEach(item => {
          delete item.date
        })
      }

      if (migratedModel.injury) {
        if (!Array.isArray(migratedModel.injury.descriptions)) {
          migratedModel.injury.descriptions = []
        }
        if (typeof migratedModel.injury.description === 'string' && migratedModel.injury.description.trim()) {
          const singleDesc = migratedModel.injury.description.trim()
          const exists = migratedModel.injury.descriptions.some(
            d => (d?.description || '').trim().toLowerCase() === singleDesc.toLowerCase(),
          )
          if (!exists) {
            migratedModel.injury.descriptions.push({
              description: singleDesc,
              provider: 'Report',
            })
          }
          delete migratedModel.injury.description
        } else if (migratedModel.injury.description !== undefined) {
          delete migratedModel.injury.description
        }
        if (!Array.isArray(migratedModel.injury.recordFiles)) {
          migratedModel.injury.recordFiles = []
        }
      }

      migratedModel = cleanAndNormalizeCaseForSave(migratedModel)
      setCaseModel(migratedModel)
      setOriginalCaseModel(migratedModel)
      return migratedModel
    })

    const filesPromise = CasesService.getFiles(caseNumber).then(setFiles)

    const appointmentsPromise = AppointmentsService.getCaseAppointments(caseNumber).then(setAppointments)

    const tasksPromise = TasksService.getAllForCase(caseNumber).then(setTasks)

    const referralSourcesPromise = ReferralSourcesService.getAll().then(setReferralSources)

    const adjustersPromise = AdjustersService.getAll().then(setAdjusters)

    Promise.all([
      casePromise,
      filesPromise,
      appointmentsPromise,
      tasksPromise,
      referralSourcesPromise,
      adjustersPromise,
    ])
      .then(() => {
        setIsLoading(false)
      })
      .catch(err => {
        showError(err)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber])

  useEffect(() => {
    if (caseModel && originalCaseModel) {
      const actuallyDirty = isModelActuallyDirty(caseModel, originalCaseModel)
      setModelIsDirty(actuallyDirty)
    }
  }, [caseModel, originalCaseModel])

  useEffect(() => {
    if (modelIsDirty) {
      window.onbeforeunload = () => true
      return () => {
        window.onbeforeunload = null
      }
    }
  }, [modelIsDirty])

  const handleNavigationAttempt = nextLocation => {
    if (isLeavingRef.current) {
      return true
    }
    if (nextLocation.pathname === location.pathname) {
      return true
    }
    if (modelIsDirty) {
      setPendingLocation(nextLocation)
      setShowConfirmLeaveModal(true)
      return false
    }
    return true
  }

  const handleConfirmLeave = () => {
    isLeavingRef.current = true
    setShowConfirmLeaveModal(false)
    setModelIsDirty(false)
    if (pendingLocation) {
      history.push(pendingLocation)
    }
  }

  const handleCancelLeave = () => {
    setShowConfirmLeaveModal(false)
    setPendingLocation(null)
  }

  useEffect(() => {
    if (caseModel) {
      setValidStateBySection(validateModel(caseModel))
    }
  }, [caseModel])

  const setModelValues = useCallback(values => {
    setCaseModel(current => {
      let newCaseModel = current
      values.forEach(v => {
        const [name, value] = v
        const path = name.replace('[', '.').replace(']', '') // change array to a format object-path recognizes
        newCaseModel = set(newCaseModel, path, value)
      })

      return newCaseModel
    })
  }, [])

  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(modelIsDirty)
    }
  }, [modelIsDirty, onDirtyStateChange])

  if (isLoading) return <LoadingSpinner />

  const safeSectionIndex =
    Number.isFinite(selectedSection) && selectedSection >= 0 && selectedSection < Sections.length ? selectedSection : 0
  const SectionComponent = Sections[safeSectionIndex].Component
  const caseTypeKey = caseModel?.caseTypeKey
  const visibleSections = Sections.filter(
    s => (!s.visibleForCaseTypes || s.visibleForCaseTypes.includes(caseTypeKey)) && !s.hideFromNav,
  )
  const groupedSections = groupBy(visibleSections, 'group')

  const handleSubmit = e => {
    if (e) e.preventDefault()
    const updatedCaseModel = checkForAlternateContact()
    setSubmitAttempted(true)
    const sanitizedModel = cleanAndNormalizeCaseForSave(updatedCaseModel)
    const validationResult = validateModel(sanitizedModel)

    if (!validationResult.success) {
      setValidStateBySection(validationResult)
      return
    }

    handleCaseSave(sanitizedModel, selectedSection)
  }

  const handleCaseSave = (updatedCaseModel, selectedSection) => {
    ;(() => {
      // Updates the case model to reflect latest additions entered by user.

      // Clean and normalize model before saving
      let modelToSave = cleanAndNormalizeCaseForSave(updatedCaseModel)
      const phonePaths = [
        'client.phone',
        'client.fax',
        'client.alternateContact.phone',
        'client.alternateContact.fax',
        'employer.contact.phone',
        'employer.contact.fax',
        'plaintiff.contact.phone',
        'plaintiff.contact.fax',
        'defendant.contact.phone',
        'defendant.contact.fax',
        'physicianReviewer.contact.phone',
        'physicianReviewer.contact.fax',
      ]

      phonePaths.forEach(path => {
        const val = get(updatedCaseModel, path)
        if (val) {
          modelToSave = set(modelToSave, path, cleanPhone(val))
        }
      })

      // Clean phone numbers in provider arrays
      const providerArrays = ['treatmentProviders', 'equipmentProviders', 'pharmacies']
      providerArrays.forEach(arrayPath => {
        const list = get(updatedCaseModel, arrayPath)
        if (Array.isArray(list)) {
          list.forEach((item, index) => {
            if (item.phone) {
              modelToSave = set(modelToSave, `${arrayPath}.${index}.phone`, cleanPhone(item.phone))
            }
            if (item.fax) {
              modelToSave = set(modelToSave, `${arrayPath}.${index}.fax`, cleanPhone(item.fax))
            }
          })
        }
      })

      // Universal recursive date cleaning & normalization
      const cleanEmptyDates = obj => {
        if (!obj || typeof obj !== 'object') return
        Object.keys(obj).forEach(key => {
          if (obj[key] && typeof obj[key] === 'object') {
            cleanEmptyDates(obj[key])
          } else if (typeof obj[key] === 'string') {
            const lowerKey = key.toLowerCase()
            const isDateField = lowerKey.includes('date') || lowerKey === 'birthdate' || lowerKey === 'dateofhire'
            const isUtcOrTimestamp = lowerKey.endsWith('utc') || lowerKey.endsWith('timestamp')
            if (isDateField && !isUtcOrTimestamp) {
              const trimmed = obj[key].trim()
              if (trimmed === '' || trimmed.toLowerCase() === 'null') {
                obj[key] = null
              } else {
                const m = Moment(
                  trimmed,
                  ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', Moment.ISO_8601],
                  true,
                )
                if (m.isValid()) {
                  obj[key] = m.format('YYYY-MM-DD')
                } else if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                  // For diagnosis date, keep freeform text if non-empty
                  if (lowerKey === 'date' && (obj.icd10 !== undefined || obj.category !== undefined)) {
                    obj[key] = trimmed
                  } else {
                    obj[key] = null
                  }
                }
              }
            }
          }
        })
      }
      cleanEmptyDates(modelToSave)

      // Normalize date fields to YYYY-MM-DD to avoid schema validation errors (e.g. ISO strings with timestamps)
      const normalizeDateString = val => {
        if (!val || typeof val !== 'string' || val.trim() === '') return val
        const m = Moment(val, ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', Moment.ISO_8601], true)
        return m.isValid() ? m.format('YYYY-MM-DD') : val
      }

      if (modelToSave.referral?.date) {
        modelToSave = set(modelToSave, 'referral.date', normalizeDateString(modelToSave.referral.date))
      }
      if (modelToSave.referral?.dueDate) {
        modelToSave = set(modelToSave, 'referral.dueDate', normalizeDateString(modelToSave.referral.dueDate))
      }
      if (modelToSave.client?.birthdate) {
        modelToSave = set(modelToSave, 'client.birthdate', normalizeDateString(modelToSave.client.birthdate))
      }
      if (modelToSave.injury?.date) {
        modelToSave = set(modelToSave, 'injury.date', normalizeDateString(modelToSave.injury.date))
      }
      if (modelToSave.mmi?.date) {
        modelToSave = set(modelToSave, 'mmi.date', normalizeDateString(modelToSave.mmi.date))
      }

      // Universal recursive address state normalization
      const STATE_MAP = {
        alabama: 'AL',
        alaska: 'AK',
        arizona: 'AZ',
        arkansas: 'AR',
        california: 'CA',
        colorado: 'CO',
        connecticut: 'CT',
        delaware: 'DE',
        florida: 'FL',
        georgia: 'GA',
        hawaii: 'HI',
        idaho: 'ID',
        illinois: 'IL',
        indiana: 'IN',
        iowa: 'IA',
        kansas: 'KS',
        kentucky: 'KY',
        louisiana: 'LA',
        maine: 'ME',
        maryland: 'MD',
        massachusetts: 'MA',
        michigan: 'MI',
        minnesota: 'MN',
        mississippi: 'MS',
        missouri: 'MO',
        montana: 'MT',
        nebraska: 'NE',
        nevada: 'NV',
        'new hampshire': 'NH',
        'new jersey': 'NJ',
        'new mexico': 'NM',
        'new york': 'NY',
        'north carolina': 'NC',
        'north dakota': 'ND',
        ohio: 'OH',
        oklahoma: 'OK',
        oregon: 'OR',
        pennsylvania: 'PA',
        'rhode island': 'RI',
        'south carolina': 'SC',
        'south dakota': 'SD',
        tennessee: 'TN',
        texas: 'TX',
        utah: 'UT',
        vermont: 'VT',
        virginia: 'VA',
        washington: 'WA',
        'west virginia': 'WV',
        wisconsin: 'WI',
        wyoming: 'WY',
        'district of columbia': 'DC',
        'american samoa': 'AS',
        guam: 'GU',
        'northern mariana islands': 'MP',
        'puerto rico': 'PR',
        'virgin islands': 'VI',
      }
      const normalizeStateValue = st => {
        if (!st || typeof st !== 'string') return st
        const cleaned = st.trim().toLowerCase()
        if (cleaned.length === 2) return st.trim().toUpperCase()
        return STATE_MAP[cleaned] || (cleaned.length === 2 ? cleaned.toUpperCase() : st.trim())
      }
      const normalizeStatesRecursively = obj => {
        if (!obj || typeof obj !== 'object') return
        Object.keys(obj).forEach(key => {
          if (obj[key] && typeof obj[key] === 'object') {
            normalizeStatesRecursively(obj[key])
          } else if (key.toLowerCase() === 'state' && typeof obj[key] === 'string') {
            obj[key] = normalizeStateValue(obj[key])
          }
        })
      }
      normalizeStatesRecursively(modelToSave)

      // Prune blank or invalid items from arrays where schema requires specific properties
      if (Array.isArray(modelToSave.medications)) {
        modelToSave.medications = modelToSave.medications.filter(
          m => m && typeof m.name === 'string' && m.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.allergies)) {
        modelToSave.allergies = modelToSave.allergies.filter(
          a => a && typeof a.name === 'string' && a.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.treatmentProviders)) {
        modelToSave.treatmentProviders = modelToSave.treatmentProviders.filter(
          p => p && typeof p.name === 'string' && p.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.equipmentProviders)) {
        modelToSave.equipmentProviders = modelToSave.equipmentProviders.filter(
          p => p && typeof p.name === 'string' && p.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.pharmacies)) {
        modelToSave.pharmacies = modelToSave.pharmacies.filter(
          p => p && typeof p.name === 'string' && p.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.equipment)) {
        modelToSave.equipment = modelToSave.equipment.filter(
          e => e && typeof e.item === 'string' && e.item.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.tests)) {
        modelToSave.tests = modelToSave.tests.filter(t => t && typeof t.type === 'string' && t.type.trim() !== '')
      }
      if (Array.isArray(modelToSave.homeSetup)) {
        modelToSave.homeSetup = modelToSave.homeSetup.filter(
          h => h && typeof h.type === 'string' && h.type.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.diagnosis)) {
        modelToSave.diagnosis = modelToSave.diagnosis.filter(
          d => d && typeof d.name === 'string' && d.name.trim() !== '',
        )
      }
      if (Array.isArray(modelToSave.surgeriesAndHospitalizations)) {
        modelToSave.surgeriesAndHospitalizations = modelToSave.surgeriesAndHospitalizations.filter(
          s => !isObjectEmpty(s),
        )
      }

      // Clean status history and mmi history before saving
      if (modelToSave.status && Array.isArray(modelToSave.status.history)) {
        const cleanedHist = modelToSave.status.history
          .filter(x => !isObjectEmpty(x))
          .map(x => {
            const item = { ...x }
            Object.keys(item).forEach(k => {
              if (typeof item[k] === 'string' && item[k].trim() === '') {
                item[k] = null
              }
            })
            if (item.dateLastWorked) {
              item.dateLastWorked = normalizeDateString(item.dateLastWorked)
            }
            if (item.date) {
              item.date = normalizeDateString(item.date)
            }
            return item
          })

        if (cleanedHist.length > 0) {
          const latest = cleanedHist[cleanedHist.length - 1]
          modelToSave = {
            ...modelToSave,
            status: {
              ...modelToSave.status,
              workStatus: latest.workStatus ?? null,
              returnStatus: latest.returnStatus ?? null,
              date: latest.date ?? null,
              treatmentProvider: latest.treatmentProvider ?? null,
              currentTreatment: latest.currentTreatment ?? null,
              dateLastWorked: latest.dateLastWorked ?? null,
              lightDuty: Boolean(latest.lightDuty),
              history: cleanedHist,
            },
          }
        } else {
          modelToSave = {
            ...modelToSave,
            status: {
              ...modelToSave.status,
              history: [],
            },
          }
        }
      }

      if (modelToSave.mmi && Array.isArray(modelToSave.mmi.history)) {
        const cleanedMmiHist = modelToSave.mmi.history
          .filter(x => !isObjectEmpty(x))
          .map(x => {
            const item = { ...x }
            Object.keys(item).forEach(k => {
              if (typeof item[k] === 'string' && item[k].trim() === '') {
                item[k] = null
              }
            })
            if (item.date) {
              item.date = normalizeDateString(item.date)
            }
            return item
          })

        if (cleanedMmiHist.length > 0) {
          const latest = cleanedMmiHist[cleanedMmiHist.length - 1]
          modelToSave = {
            ...modelToSave,
            mmi: {
              ...modelToSave.mmi,
              date: latest.date || null,
              mmiDate: latest.date || null,
              reached: latest.isActual ?? null,
              isMmiReached: latest.isActual ?? null,
              history: cleanedMmiHist,
            },
          }
        } else {
          modelToSave = {
            ...modelToSave,
            mmi: {
              ...modelToSave.mmi,
              history: [],
            },
          }
        }
      }

      setCaseModel(modelToSave)
      setIsLoading(true)
      setSaveError(null)

      return CasesService.update(modelToSave)
    })()
      .onSuccess(savedCase => {
        setSaveError(null)
        showError(null)
        setModelIsDirty(false)

        // mark files as now saved
        setFiles(files.map(x => ({ ...x, hasBeenSaved: true })))

        if (savedCase) {
          setCaseModel(savedCase)
          setOriginalCaseModel(savedCase)
        }

        // display the case save success message
        setIsSuccessMessageVisible(true)
      })
      .onBadRequest(err => {
        setSaveError(err || 'Failed to save case. Please check for errors.')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .catch(err => {
        setSaveError(
          typeof err === 'string' ? err : err?.message || 'An unexpected error occurred while trying to save the case',
        )
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
      .then(() => {
        setIsLoading(false)
      })
  }

  const handleNewCaseFileAttached = file => {
    const newFiles = files.concat(file)
    setFiles(newFiles)
  }

  const setModelValue = (name, value) => {
    const path = name.replace('[', '.').replace(']', '') // change array to a format object-path recognizes
    setCaseModel(current => set(current, path, value))
  }

  const handleReferralSourceCreated = r => {
    const index = referralSources.findIndex(source => source.id === r.id)

    if (index !== null) {
      const newReferralSourcesArray = referralSources.slice()
      newReferralSourcesArray.splice(index, 1, r)

      setReferralSources(sortByName(newReferralSourcesArray))
    } else {
      setReferralSources(sortByName(referralSources.concat(r)))
    }

    setModelValue('referral.sourceId', r.id)
    setSelectedReferralSource({})
    setShowNewReferralSourceModal(false)
  }

  const handleAdjusterCreated = r => {
    const index = adjusters.findIndex(adjuster => adjuster.id === r.id)

    if (index !== null) {
      const newAdjustersArray = adjusters.slice()
      newAdjustersArray.splice(index, 1, r)

      setAdjusters(sortByName(newAdjustersArray))
    } else {
      setAdjusters(sortByName(adjusters.concat(r)))
    }

    setModelValue('referral.adjusterId', r.id)
    setSelectedAdjuster({})
    setShowNewAdjusterModal(false)
  }

  const getCaseModelValue = (name, defaultValue, model = caseModel) => {
    return get(name, model) || defaultValue
  }

  const handleBlur = name => {
    setTouched({
      ...touched,
      [name]: true,
    })
  }

  const createInputProps = name => ({
    id: name,
    name,
    value: getCaseModelValue(name, ''),
    onChange: e => {
      const value = (() => {
        if (e.target) {
          switch (e.target.type) {
            case 'checkbox':
              return e.target.checked
            default:
              return e.target.value
          }
        } else {
          // the richtext control sends the text as the value, so in that event, just return that value as the input
          return e
        }
      })()
      setModelValue(name, value)
    },
    onBlur: e => handleBlur(name),
  })

  const removeItemFromArray = (name, index) => {
    const orig = getCaseModelValue(name, [])
    const newArray = [...orig.slice(0, index), ...orig.slice(index + 1)]
    setModelValue(name, newArray)
  }

  const addItemToArray = (name, item) => {
    const newArray = getCaseModelValue(name, []).concat(item)
    setModelValue(name, newArray)
    return newArray.length - 1
  }

  const ErrorMessageForSection =
    sectionIndex =>
    ({ name, className, ...props }) => {
      if (!touched[name] && !submitAttempted) return null

      const validState = validStateBySection[sectionIndex]
      if (!validStateBySection[sectionIndex].errors) return null

      const msg = validState.errors[name]
      if (!msg) return null

      return (
        <div className={`text-danger form-text ${className}`} {...props}>
          {msg}
        </div>
      )
    }

  const checkForAlternateContact = () => {
    if (getCaseModelValue('client.hasAlternateContact', false) === false) {
      const newCaseModel = {
        ...caseModel,
        client: {
          ...caseModel.client,
          alternateContact: {
            firstName: '',
            lastName: '',
            phone: '',
            fax: '',
            email: '',
          },
        },
      }
      return newCaseModel
    }
    return caseModel
  }

  const toggleSidebar = () => {
    setIsSidebarToggled(!isSidebarToggled)
  }

  const getStateByName = name => namedState[name]
  const setStateByName = (name, value) => {
    setNamedState({ ...namedState, [name]: value })
  }

  function sectionChange(sectionIndex) {
    setSelectedSection(sectionIndex)
    history.replace(location.pathname + '?section=' + sectionIndex)
    window.scrollTo(0, 0)
  }

  function showAdjusterEditScreen(adjusterId) {
    if (adjusters) {
      const adjuster = adjusters.find(adjuster => adjuster.id === adjusterId) || {}
      setSelectedAdjuster(adjuster)
      setShowNewAdjusterModal(true)
    }
  }

  function showReferralSourcerEditScreen(referralSourceId) {
    if (referralSources) {
      const source = referralSources.find(source => source.id === referralSourceId) || {}
      setSelectedReferralSource(source)
      setShowNewReferralSourceModal(true)
    }
  }

  return (
    <div className="case-details-component">
      <Prompt when={modelIsDirty} message={handleNavigationAttempt} />
      <div className={`fixed-left-container ${!isSidebarToggled ? 'fixed-left-container-closed' : ''}`}>
        <button className="close-button" onClick={toggleSidebar} type="button">
          <i className={`fas fa-lg ${isSidebarToggled ? 'fa-arrow-alt-circle-left' : 'fa-arrow-alt-circle-right'}`}></i>
        </button>
        <div className={`side-bar-container ${!isSidebarToggled ? 'side-bar-container-closed' : ''} bg-light`}>
          <div className="side-bar-background bg-light" />
          <ul className="nav flex-column nav-pills pt-5">
            {Object.keys(groupedSections).map((groupName, groupIndex) => {
              return (
                <div key={`groupName-${groupIndex}`}>
                  <p className="tab-header mb-0 mt-2">{groupName.toUpperCase()}</p>
                  {!!groupedSections &&
                    !!groupedSections[groupName] &&
                    groupedSections[groupName].map((section, sectionIndex) => {
                      return (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                        <li
                          key={section.originalIndex}
                          id={`section-${groupIndex}-${sectionIndex}`}
                          className={`nav-link ${
                            (!!validStateBySection && validStateBySection[section.originalIndex].success) ||
                            !submitAttempted
                              ? 'tab-valid'
                              : 'tab-error'
                          } ${section.originalIndex === selectedSection && 'active'}`}
                          onClick={() => sectionChange(section.originalIndex)}
                        >
                          {section.name}
                        </li>
                      )
                    })}
                </div>
              )
            })}
          </ul>
          <div className="px-3 pt-2 pb-3">
            <button
              type="button"
              className="btn btn-sm btn-primary w-100"
              onClick={() => {
                const apptNotes = Sections.find(s => s.name === 'Appointment Notes')
                if (apptNotes) sectionChange(apptNotes.originalIndex)
              }}
            >
              <i className="fas fa-calendar-check mr-1" />
              Completed Appointments
            </button>
          </div>
        </div>
      </div>
      <form ref={formRef} className="detail-page-form case-details-form" onSubmit={handleSubmit}>
        <div className="d-flex w-100">
          <div className={`w-100 p-4 section-container ${!isSidebarToggled ? 'section-container-closed' : ''}`}>
            {saveError && (
              <Alert variant="danger" dismissible onClose={() => setSaveError(null)} className="mb-4">
                <Alert.Heading style={{ fontSize: '1.05rem', fontWeight: 600 }}>Case Save Failed</Alert.Heading>
                <div style={{ whiteSpace: 'pre-line' }}>{saveError}</div>
              </Alert>
            )}
            <SectionComponent
              caseNumber={caseNumber}
              caseModel={caseModel}
              caseFiles={files}
              handleNewCaseFileAttached={handleNewCaseFileAttached}
              createInputProps={createInputProps}
              getValue={getCaseModelValue}
              setModelValue={setModelValue}
              addItemToArray={addItemToArray}
              removeItemFromArray={removeItemFromArray}
              referralSources={referralSources}
              onAddNewReferralSource={() => setShowNewReferralSourceModal(true)}
              adjusters={adjusters}
              onAddNewAdjuster={() => setShowNewAdjusterModal(true)}
              onEditAdjuster={showAdjusterEditScreen}
              onEditReferralSource={showReferralSourcerEditScreen}
              tasks={tasks}
              ErrorMessage={ErrorMessageForSection(selectedSection)}
              validStateBySection={validStateBySection}
              namedState={namedState}
              modelIsDirty={modelIsDirty}
              getStateByName={getStateByName}
              setStateByName={setStateByName}
              appointments={appointments}
              user={user}
              setModelValues={setModelValues}
            />
          </div>
        </div>
      </form>
      <div className="content-background" />
      {showNewReferralSourceModal && (
        <NewCompanyModal
          oldModel={selectedReferralSource}
          onSaveSuccess={handleReferralSourceCreated}
          onClosed={() => {
            setSelectedReferralSource({})
            setShowNewReferralSourceModal(false)
          }}
        />
      )}
      {showNewAdjusterModal && (
        <NewAdjusterModal
          oldModel={selectedAdjuster}
          onSaveSuccess={handleAdjusterCreated}
          referralSources={referralSources}
          onClosed={() => {
            setSelectedAdjuster({})
            setShowNewAdjusterModal(false)
          }}
        />
      )}
      <Modal show={showConfirmLeaveModal} onHide={handleCancelLeave}>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>You have unsaved changes. Are you sure you want to leave without saving?</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={handleCancelLeave}>
            Keep Editing
          </button>
          <button className="btn btn-danger" onClick={handleConfirmLeave}>
            Leave Page
          </button>
        </Modal.Footer>
      </Modal>
      <div className="floating-alert-container">
        <Alert
          id="success-message"
          variant="success"
          className={`floating-alert ${isSuccessMessageVisible ? 'show-message' : ''}`}
        >
          <strong>Case save successful!</strong>
        </Alert>
      </div>
    </div>
  )
}

export default DetailsPage
